// SCR-PUBLIC-014 — تأیید اصالت نسخه و برگه درخواست با QR (/v/:code؛ BR-MED-013، AC-MED-040.2؛ C-AGENT-874): فقط خلاصه غیرحساس (نوع، شماره،
// تاریخ، وضعیت معتبر/باطل، نام و کد نظام پزشک، تعداد اقلام) بدون نام مراجعه‌کننده یا دارو. کد نامعتبر ← پیام عمومی.
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap } from '@phoenix/api-client';
import { usePublicApi } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, JalaliDate, LoadingState } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { SCR_PUBLIC_014 } from '../common';

export function PublicVerifyPage() {
  const { t } = useTranslation('med');
  const api = usePublicApi();
  const { code = '' } = useParams();
  const query = useQuery({
    queryKey: ['medorders-public-verify', code],
    queryFn: () => unwrap(api.GET('/api/v1/medical-orders/public/verify/{code}', { params: { path: { code } } })),
    retry: false,
  });
  useEffect(() => {
    document.title = `${t('med:verify.title')} · ${t('shell:app.staff_name')}`;
  }, [t]);
  const data = query.data;
  const valid = data?.status === 'Valid';
  return (
    <Box data-screen-id={SCR_PUBLIC_014} sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 1.5, sm: 3 } }}>
      <Paper variant="outlined" component="main" sx={{ p: { xs: 2, sm: 4 }, maxWidth: 640, mx: 'auto' }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center' }}>
          <VerifiedOutlinedIcon color="primary" />
          <Typography variant="h1">{t('med:verify.title')}</Typography>
        </Stack>
        {query.isPending ? (
          <LoadingState rows={3} />
        ) : query.error ? (
          isApiError(query.error) && query.error.status === 404 ? (
            <Alert severity="warning" data-testid="med-verify-invalid">
              {t('med:verify.invalid')}
            </Alert>
          ) : (
            <ErrorState error={query.error} onRetry={() => void query.refetch()} />
          )
        ) : data ? (
          <Stack spacing={1} data-testid="med-verify-result">
            <Alert severity={valid ? 'success' : 'error'} data-testid="med-verify-status" data-status={data.status}>
              {t(`med:verify.status.${data.status}`, { defaultValue: data.status })}
            </Alert>
            <Typography>
              {t(`med:verify.kind.${data.kind}`, { defaultValue: data.kind })}: <bdi dir="ltr">{data.number}</bdi>
            </Typography>
            <Typography>
              {t('med:verify.date')}: <JalaliDate value={data.issuedOn} />
            </Typography>
            {data.providerName && (
              <Typography>
                {t('med:verify.provider')}: {data.providerName}
                {data.councilNumber ? ` · ${t('med:verify.council', { number: faDigits(data.councilNumber) })}` : ''}
              </Typography>
            )}
            <Typography>{t('med:verify.items', { count: Number(data.itemCount) })}</Typography>
          </Stack>
        ) : null}
      </Paper>
    </Box>
  );
}
